import { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
  Divider,
  Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SaveIcon from '@mui/icons-material/Save'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import InputAdornment from '@mui/material/InputAdornment'
import {
  SchedulerPackagingRoutesRowType,
  ProductionRequirementRowType,
} from './Scheduler.types'

// ─── Types ───────────────────────────────────────────────────────────────────

type TimeWindowBarProps = {
  startHour: number | null
  endHour: number | null
  onChange: (start: number | null, end: number | null) => void
}

type ProductEditRowProps = {
  route: SchedulerPackagingRoutesRowType
  requirement: ProductionRequirementRowType | null
  lineName: string
  lineRunRate: string
  index: number
  onRouteChange: (
    routeId: number,
    field: string,
    value: string,
    fullRow: Record<string, unknown>
  ) => void
  onRequirementChange: (
    reqId: number,
    field: string,
    value: string | number,
    fullRow: Record<string, unknown>
  ) => void
}

type ProductEditPanelProps = {
  lineName: string
  packagingRoutes: SchedulerPackagingRoutesRowType[]
  productionRequirements: ProductionRequirementRowType[]
  selectedWeek: number
  onWeekChange: (week: number) => void
  onSaveRoutes: (
    api: string,
    data: Record<number, Record<string, unknown>>,
    week?: number
  ) => void
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TOTAL_HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HANDLE_WIDTH = 4

// ─── TimeWindowBar ───────────────────────────────────────────────────────────

const TimeWindowBar = memo(
  ({ startHour, endHour, onChange }: TimeWindowBarProps) => {
    const barRef = useRef<HTMLDivElement>(null)
    const draggingRef = useRef<'start' | 'end' | null>(null)
    // Local state that persists dragged values (props don't update until save)
    const [localStart, setLocalStart] = useState(startHour ?? 0)
    const [localEnd, setLocalEnd] = useState(endHour ?? TOTAL_HOURS)
    const currentStartRef = useRef(localStart)
    const currentEndRef = useRef(localEnd)
    const [dragHour, setDragHour] = useState<number | null>(null)
    const [dragSide, setDragSide] = useState<'start' | 'end' | null>(null)
    const [hovered, setHovered] = useState<'start' | 'end' | null>(null)

    // Sync from props only when they actually change from the server
    useEffect(() => {
      setLocalStart(startHour ?? 0)
      setLocalEnd(endHour ?? TOTAL_HOURS)
      currentStartRef.current = startHour ?? 0
      currentEndRef.current = endHour ?? TOTAL_HOURS
    }, [startHour, endHour])

    const getHourFromMouse = useCallback((clientX: number): number => {
      if (!barRef.current) return 0
      const rect = barRef.current.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      return Math.round(ratio * TOTAL_HOURS)
    }, [])

    const handleMouseDown = useCallback(
      (side: 'start' | 'end') => (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        draggingRef.current = side
        setDragSide(side)
        const hour = getHourFromMouse(e.clientX)
        setDragHour(hour)
      },
      [getHourFromMouse]
    )

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!draggingRef.current) return
        const hour = getHourFromMouse(e.clientX)
        setDragHour(hour)

        if (draggingRef.current === 'start') {
          currentStartRef.current = Math.max(
            0,
            Math.min(hour, currentEndRef.current - 1)
          )
        } else {
          currentEndRef.current = Math.min(
            TOTAL_HOURS,
            Math.max(hour, currentStartRef.current + 1)
          )
        }
      }

      const handleMouseUp = () => {
        if (!draggingRef.current) return
        setLocalStart(currentStartRef.current)
        setLocalEnd(currentEndRef.current)
        onChange(currentStartRef.current, currentEndRef.current)
        draggingRef.current = null
        setDragSide(null)
        setDragHour(null)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }, [getHourFromMouse, onChange])

    const startPct = (localStart / TOTAL_HOURS) * 100
    const endPct = (localEnd / TOTAL_HOURS) * 100
    const dragStartPct =
      dragSide === 'start' && dragHour != null
        ? (Math.min(dragHour, currentEndRef.current - 1) / TOTAL_HOURS) * 100
        : startPct
    const dragEndPct =
      dragSide === 'end' && dragHour != null
        ? (Math.max(dragHour, currentStartRef.current + 1) / TOTAL_HOURS) * 100
        : endPct

    return (
      <Box sx={{ width: '100%', userSelect: 'none' }}>
        <Box
          ref={barRef}
          sx={{
            position: 'relative',
            height: 28,
            bgcolor: '#f0f0f0',
            borderRadius: 0.5,
            overflow: 'visible',
            cursor: draggingRef.current ? 'grabbing' : 'default',
          }}
        >
          {/* 6-hour tick marks */}
          {Array.from({ length: 28 }, (_, i) => (i + 1) * 6).map((h) => (
            <Box
              key={`tick-${h}`}
              sx={{
                position: 'absolute',
                left: `${(h / TOTAL_HOURS) * 100}%`,
                top: 0,
                bottom: 0,
                width: h % 24 === 0 ? '1.5px' : '0.5px',
                bgcolor: h % 24 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* Filled area */}
          <Box
            sx={{
              position: 'absolute',
              left: `${dragStartPct}%`,
              width: `${dragEndPct - dragStartPct}%`,
              top: 0,
              bottom: 0,
              bgcolor: 'rgba(25, 118, 210, 0.25)',
              pointerEvents: 'none',
            }}
          />

          {/* Start handle */}
          <Box
            onMouseDown={handleMouseDown('start')}
            onMouseEnter={() => setHovered('start')}
            onMouseLeave={() => setHovered(null)}
            sx={{
              position: 'absolute',
              left: `${dragStartPct}%`,
              top: -2,
              bottom: -2,
              width: 8,
              bgcolor:
                dragSide === 'start' || hovered === 'start'
                  ? '#1565c0'
                  : '#1976d2',
              cursor: draggingRef.current ? 'grabbing' : 'grab',
              borderRadius: 0.5,
              transform: 'translateX(-50%)',
              zIndex: 2,
              transition:
                dragSide === 'start' ? 'none' : 'background-color 0.15s',
              '&:hover': { bgcolor: '#1565c0', width: 10 },
            }}
          />

          {/* End handle */}
          <Box
            onMouseDown={handleMouseDown('end')}
            onMouseEnter={() => setHovered('end')}
            onMouseLeave={() => setHovered(null)}
            sx={{
              position: 'absolute',
              left: `${dragEndPct}%`,
              top: -2,
              bottom: -2,
              width: 8,
              bgcolor:
                dragSide === 'end' || hovered === 'end' ? '#1565c0' : '#1976d2',
              cursor: draggingRef.current ? 'grabbing' : 'grab',
              borderRadius: 0.5,
              transform: 'translateX(-50%)',
              zIndex: 2,
              transition:
                dragSide === 'end' ? 'none' : 'background-color 0.15s',
              '&:hover': { bgcolor: '#1565c0', width: 10 },
            }}
          />

          {/* Drag tooltip */}
          {dragSide && dragHour != null && (
            <Box
              sx={{
                position: 'absolute',
                left: `${(dragHour / TOTAL_HOURS) * 100}%`,
                top: -22,
                transform: 'translateX(-50%)',
                bgcolor: '#1976d2',
                color: '#fff',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.6rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              {dragHour}h
            </Box>
          )}
        </Box>

        {/* Day labels and current values */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 0.25,
          }}
        >
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
            Start: {localStart}h
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'space-around',
              mx: 1,
            }}
          >
            {DAY_LABELS.map((d, i) => (
              <Typography
                key={d}
                sx={{
                  fontSize: '0.5rem',
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                {d}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
            End: {localEnd}h
          </Typography>
        </Box>
      </Box>
    )
  }
)

TimeWindowBar.displayName = 'TimeWindowBar'

// ─── ProductEditRow ──────────────────────────────────────────────────────────

const ProductEditRow = memo(
  ({
    route,
    requirement,
    lineName,
    lineRunRate,
    index,
    onRouteChange,
    onRequirementChange,
  }: ProductEditRowProps) => {
    const [expanded, setExpanded] = useState(false)

    // Local field state for expanded section
    const [convFactor, setConvFactor] = useState(
      requirement?.conversionFactor ?? ''
    )
    const [reqTemp, setReqTemp] = useState<string>(
      requirement?.requiredTemperature != null
        ? String(requirement.requiredTemperature)
        : ''
    )
    const [prFactor, setPrFactor] = useState(route.pr ?? '')
    const [kitchenRate, setKitchenRate] = useState(route.kitchenRunRate ?? '')
    const [minRunTime, setMinRunTime] = useState(route.minRunTime ?? '')
    const [maxRunTime, setMaxRunTime] = useState(route.maxRunTime ?? '')
    const [prepTime, setPrepTime] = useState(route.prepTime ?? '')
    const [inputPrepTime, setInputPrepTime] = useState(
      route.inputPrepTime ?? ''
    )
    const [maxBatches, setMaxBatches] = useState(route.maxBatches ?? '')
    const [quantity, setQuantity] = useState(requirement?.quantity ?? '')

    // Build a stub requirement row for products without one (enables editing to create it)
    const effectiveReq = useMemo(() => {
      if (requirement) return requirement
      return {
        id: -(route.id + 10000), // negative ID signals a new row
        productCode: route.productCode,
        productName: route.productName,
        inputProduct: '',
        quantity: '',
        maxQuantity: '',
        conversionFactor: '',
        requiredTemperature: null,
        dontStartBefore: null,
        finishBefore: null,
        weekNumber: 0, // will be set by the save handler
      } as ProductionRequirementRowType
    }, [requirement, route])

    const handleTimeWindowChange = useCallback(
      (start: number | null, end: number | null) => {
        const fullRow = { ...effectiveReq } as unknown as Record<
          string,
          unknown
        >
        if (start !== effectiveReq.dontStartBefore) {
          onRequirementChange(
            effectiveReq.id,
            'dontStartBefore',
            start ?? '',
            fullRow
          )
        }
        if (end !== effectiveReq.finishBefore) {
          onRequirementChange(
            effectiveReq.id,
            'finishBefore',
            end ?? '',
            fullRow
          )
        }
      },
      [effectiveReq, onRequirementChange]
    )

    const handleRouteFieldChange = useCallback(
      (field: string, value: string) => {
        const fullRow = { ...route } as unknown as Record<string, unknown>
        onRouteChange(route.id, field, value, fullRow)
      },
      [route, onRouteChange]
    )

    const handleReqFieldChange = useCallback(
      (field: string, value: string | number) => {
        const fullRow = { ...effectiveReq } as unknown as Record<
          string,
          unknown
        >
        onRequirementChange(effectiveReq.id, field, value, fullRow)
      },
      [requirement, onRequirementChange]
    )

    return (
      <Box
        sx={{
          bgcolor: index % 2 === 0 ? '#fff' : '#fafafa',
          borderBottom: '1px solid #eee',
        }}
      >
        {/* Main row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            minHeight: 70,
            gap: 1,
          }}
        >
          {/* Product info */}
          <Box sx={{ width: 180, flexShrink: 0 }}>
            <Typography
              sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 }}
              noWrap
            >
              {route.productCode}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
              noWrap
            >
              {route.productName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              <Chip
                label={route.kitchen}
                size="small"
                sx={{ height: 18, fontSize: '0.55rem', bgcolor: '#e8eaf6' }}
              />
              <Chip
                label={route.baseProductCode}
                size="small"
                sx={{ height: 18, fontSize: '0.55rem', bgcolor: '#f3e5f5' }}
              />
            </Box>
          </Box>

          {/* Quantity */}
          <Box sx={{ width: 100, flexShrink: 0 }}>
            <TextField
              size="small"
              type="number"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value)
                handleReqFieldChange('quantity', e.target.value)
              }}
              sx={{
                '& .MuiInputBase-root': { height: 28, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { px: 1, py: 0.5 },
              }}
              fullWidth
              placeholder="Qty"
            />
            <Typography
              sx={{ fontSize: '0.5rem', color: 'text.secondary', mt: 0.25 }}
            >
              Quantity
            </Typography>
          </Box>

          {/* Time window bar */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <TimeWindowBar
              startHour={requirement?.dontStartBefore ?? null}
              endHour={requirement?.finishBefore ?? null}
              onChange={handleTimeWindowChange}
            />
          </Box>

          {/* Run rate */}
          <Box sx={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
            <Chip
              label={`${lineRunRate || '—'} u/hr`}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.6rem' }}
            />
          </Box>

          {/* Expand toggle */}
          <Box sx={{ width: 30, flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>
        </Box>

        {/* Expanded section */}
        {expanded && (
          <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
            <Divider sx={{ mb: 1 }} />
            {/* Row 1 */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
              <CompactField
                label="Conv. Factor"
                value={convFactor}
                onChange={(v) => {
                  setConvFactor(v)
                  handleReqFieldChange('conversionFactor', v)
                }}
                disabled={!requirement}
              />
              <Box sx={{ minWidth: 90 }}>
                <Typography
                  sx={{
                    fontSize: '0.55rem',
                    color: 'text.secondary',
                    mb: 0.25,
                  }}
                >
                  Required Temp
                </Typography>
                <Select
                  size="small"
                  value={reqTemp}
                  onChange={(e) => {
                    setReqTemp(e.target.value)
                    handleReqFieldChange('requiredTemperature', e.target.value)
                  }}
                  disabled={!requirement}
                  displayEmpty
                  sx={{
                    height: 26,
                    fontSize: '0.7rem',
                    minWidth: 80,
                    '& .MuiSelect-select': { py: 0.25, px: 1 },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.7rem' }}>
                    —
                  </MenuItem>
                  <MenuItem value="132" sx={{ fontSize: '0.7rem' }}>
                    132
                  </MenuItem>
                  <MenuItem value="135" sx={{ fontSize: '0.7rem' }}>
                    135
                  </MenuItem>
                </Select>
              </Box>
              <CompactField
                label="PR Factor"
                value={prFactor}
                onChange={(v) => {
                  setPrFactor(v)
                  handleRouteFieldChange('pr', v)
                }}
              />
            </Box>
            {/* Row 2 */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <CompactField
                label="Kitchen Rate"
                value={kitchenRate}
                onChange={(v) => {
                  setKitchenRate(v)
                  handleRouteFieldChange('kitchenRunRate', v)
                }}
              />
              <CompactField
                label="Min Run Time"
                value={minRunTime}
                onChange={(v) => {
                  setMinRunTime(v)
                  handleRouteFieldChange('minRunTime', v)
                }}
              />
              <CompactField
                label="Max Run Time"
                value={maxRunTime}
                onChange={(v) => {
                  setMaxRunTime(v)
                  handleRouteFieldChange('maxRunTime', v)
                }}
              />
              <CompactField
                label="Prep Time (Off-set)"
                value={prepTime}
                onChange={(v) => {
                  setPrepTime(v)
                  handleRouteFieldChange('prepTime', v)
                }}
              />
              <CompactField
                label="Input Prep Time"
                value={inputPrepTime}
                onChange={(v) => {
                  setInputPrepTime(v)
                  handleRouteFieldChange('inputPrepTime', v)
                }}
              />
              <CompactField
                label="Max Batches"
                value={maxBatches}
                onChange={(v) => {
                  setMaxBatches(v)
                  handleRouteFieldChange('maxBatches', v)
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    )
  }
)

ProductEditRow.displayName = 'ProductEditRow'

// ─── CompactField ────────────────────────────────────────────────────────────

const CompactField = memo(
  ({
    label,
    value,
    onChange,
    disabled = false,
  }: {
    label: string
    value: string
    onChange: (value: string) => void
    disabled?: boolean
  }) => (
    <Box sx={{ minWidth: 80 }}>
      <Typography
        sx={{ fontSize: '0.55rem', color: 'text.secondary', mb: 0.25 }}
      >
        {label}
      </Typography>
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sx={{
          '& .MuiInputBase-root': { height: 26, fontSize: '0.7rem' },
          '& .MuiInputBase-input': { px: 1, py: 0.25 },
        }}
        fullWidth
      />
    </Box>
  )
)

CompactField.displayName = 'CompactField'

// ─── ProductEditPanel ────────────────────────────────────────────────────────

export const ProductEditPanel = ({
  lineName,
  packagingRoutes,
  productionRequirements,
  selectedWeek,
  onWeekChange,
  onSaveRoutes,
}: ProductEditPanelProps) => {
  const pendingRouteChanges = useRef<Record<number, Record<string, unknown>>>(
    {}
  )
  const pendingReqChanges = useRef<Record<number, Record<string, unknown>>>({})
  const [hasChanges, setHasChanges] = useState(false)

  // Filter routes for the selected line and determine the correct run rate field
  const lineRoutes = useMemo(() => {
    const results: {
      route: SchedulerPackagingRoutesRowType
      lineRunRate: string
    }[] = []
    for (const route of packagingRoutes) {
      if (route.packingLine1 === lineName) {
        results.push({ route, lineRunRate: route.lineRunRate1 })
      } else if (route.packingLine2 === lineName) {
        results.push({ route, lineRunRate: route.lineRunRate2 })
      } else if (route.packingLine3 === lineName) {
        results.push({ route, lineRunRate: route.lineRunRate3 })
      } else if (route.packingLine4 === lineName) {
        results.push({ route, lineRunRate: route.lineRunRate4 })
      }
    }
    return results
  }, [packagingRoutes, lineName])

  // Build a lookup for requirements by productCode
  // weekNumber may be undefined if the API already filters by week
  const requirementMap = useMemo(() => {
    const map = new Map<string, ProductionRequirementRowType>()
    for (const req of productionRequirements) {
      if (
        req.weekNumber == null ||
        Number(req.weekNumber) === Number(selectedWeek)
      ) {
        map.set(String(req.productCode), req)
      }
    }
    return map
  }, [productionRequirements, selectedWeek])

  // Search, sort, filter state
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<
    'productCode' | 'quantity' | 'lineRunRate'
  >('productCode')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const handleSort = useCallback(
    (field: typeof sortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortDir(field === 'productCode' ? 'asc' : 'desc')
      }
    },
    [sortField]
  )

  const filteredSortedRoutes = useMemo(() => {
    let items = lineRoutes
    if (search) {
      const s = search.toLowerCase()
      items = items.filter(
        ({ route }) =>
          String(route.productCode).toLowerCase().includes(s) ||
          (route.productName && route.productName.toLowerCase().includes(s)) ||
          (route.baseProductCode &&
            String(route.baseProductCode).toLowerCase().includes(s)) ||
          (route.kitchen && route.kitchen.toLowerCase().includes(s))
      )
    }
    items = [...items].sort((a, b) => {
      if (sortField === 'productCode') {
        return sortDir === 'asc'
          ? String(a.route.productCode).localeCompare(
              String(b.route.productCode)
            )
          : String(b.route.productCode).localeCompare(
              String(a.route.productCode)
            )
      }
      if (sortField === 'quantity') {
        const aq =
          parseFloat(
            String(requirementMap.get(String(a.route.productCode))?.quantity)
          ) || 0
        const bq =
          parseFloat(
            String(requirementMap.get(String(b.route.productCode))?.quantity)
          ) || 0
        return sortDir === 'asc' ? aq - bq : bq - aq
      }
      if (sortField === 'lineRunRate') {
        const ar = parseFloat(a.lineRunRate) || 0
        const br = parseFloat(b.lineRunRate) || 0
        return sortDir === 'asc' ? ar - br : br - ar
      }
      return 0
    })
    return items
  }, [lineRoutes, search, sortField, sortDir, requirementMap])

  const handleRouteChange = useCallback(
    (
      routeId: number,
      field: string,
      value: string,
      fullRow: Record<string, unknown>
    ) => {
      if (!pendingRouteChanges.current[routeId]) {
        pendingRouteChanges.current[routeId] = { ...fullRow }
      }
      pendingRouteChanges.current[routeId][field] = value
      setHasChanges(true)
    },
    []
  )

  const handleRequirementChange = useCallback(
    (
      reqId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => {
      if (!pendingReqChanges.current[reqId]) {
        pendingReqChanges.current[reqId] = { ...fullRow }
      }
      pendingReqChanges.current[reqId][field] = value
      setHasChanges(true)
    },
    []
  )

  const handleSave = useCallback(() => {
    const routeChanges = pendingRouteChanges.current
    const reqChanges = pendingReqChanges.current

    if (Object.keys(routeChanges).length > 0) {
      onSaveRoutes('packing-routes', routeChanges)
    }
    if (Object.keys(reqChanges).length > 0) {
      onSaveRoutes('production-requirements', reqChanges, selectedWeek)
    }

    pendingRouteChanges.current = {}
    pendingReqChanges.current = {}
    setHasChanges(false)
  }, [onSaveRoutes, selectedWeek])

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#fafafa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
            {lineName}
          </Typography>
          <Chip
            label={`${filteredSortedRoutes.length}${search ? ` / ${lineRoutes.length}` : ''} product${lineRoutes.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ height: 20, fontSize: '0.6rem' }}
          />
          <Divider orientation="vertical" flexItem />
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            Week
          </Typography>
          <Select
            value={selectedWeek}
            onChange={(e) => onWeekChange(Number(e.target.value))}
            size="small"
            sx={{
              fontSize: '0.7rem',
              height: 24,
              minWidth: 50,
              '& .MuiSelect-select': { py: 0.25 },
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => (
              <MenuItem key={w} value={w} sx={{ fontSize: '0.7rem' }}>
                {w}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {hasChanges && (
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleSave}
            sx={{
              fontSize: '0.65rem',
              textTransform: 'none',
              height: 26,
              px: 1.5,
            }}
          >
            Save Changes
          </Button>
        )}
      </Box>

      {/* Search + sort bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <TextField
          placeholder="Search products..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            flex: 1,
            maxWidth: 250,
            '& .MuiInputBase-root': { height: 26, fontSize: '0.7rem' },
            '& .MuiInputBase-input': { py: 0.25 },
          }}
        />
        <Divider orientation="vertical" flexItem />
        {(['productCode', 'quantity', 'lineRunRate'] as const).map((field) => {
          const labels = {
            productCode: 'Name',
            quantity: 'Qty',
            lineRunRate: 'Rate',
          }
          return (
            <Chip
              key={field}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  {labels[field]}
                  {sortField === field &&
                    (sortDir === 'asc' ? (
                      <ArrowUpwardIcon sx={{ fontSize: 10 }} />
                    ) : (
                      <ArrowDownwardIcon sx={{ fontSize: 10 }} />
                    ))}
                </Box>
              }
              size="small"
              variant={sortField === field ? 'filled' : 'outlined'}
              color={sortField === field ? 'primary' : 'default'}
              onClick={() => handleSort(field)}
              sx={{ height: 22, fontSize: '0.6rem', cursor: 'pointer' }}
            />
          )
        })}
      </Box>

      {/* Product rows */}
      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {filteredSortedRoutes.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {search
                ? `No products match "${search}"`
                : 'No products assigned to this line.'}
            </Typography>
          </Box>
        )}
        {filteredSortedRoutes.map(({ route, lineRunRate }, idx) => (
          <ProductEditRow
            key={route.id}
            route={route}
            requirement={requirementMap.get(String(route.productCode)) ?? null}
            lineName={lineName}
            lineRunRate={lineRunRate}
            index={idx}
            onRouteChange={handleRouteChange}
            onRequirementChange={handleRequirementChange}
          />
        ))}
      </Box>
    </Paper>
  )
}

// ─── Kitchen Product Edit Panel ─────────────────────────────────────────────
// Shows packaging routes grouped by base material for a selected kitchen.
// Each base material expands to show its products with editable fields.

type KitchenProductEditPanelProps = {
  kitchenName: string
  packagingRoutes: SchedulerPackagingRoutesRowType[]
  onSaveRoutes: (
    api: string,
    data: Record<number, Record<string, unknown>>,
    week?: number
  ) => void
}

type BaseMaterialGroup = {
  code: string
  name: string
  kitchenRunRate: string
  products: SchedulerPackagingRoutesRowType[]
}

const KitchenProductRow = memo(
  ({
    route,
    onRouteChange,
  }: {
    route: SchedulerPackagingRoutesRowType
    onRouteChange: (
      routeId: number,
      field: string,
      value: string,
      fullRow: Record<string, unknown>
    ) => void
  }) => {
    const [expanded, setExpanded] = useState(false)
    const [maxRun, setMaxRun] = useState(route.maxRunTime ?? '')
    const [minRun, setMinRun] = useState(route.minRunTime ?? '')
    const [maxBatches, setMaxBatches] = useState(route.maxBatches ?? '')
    const [prepTime, setPrepTime] = useState(route.prepTime ?? '')
    const [inputPrepTime, setInputPrepTime] = useState(
      route.inputPrepTime ?? ''
    )

    const handleChange = useCallback(
      (field: string, value: string) => {
        onRouteChange(
          route.id,
          field,
          value,
          route as unknown as Record<string, unknown>
        )
      },
      [route, onRouteChange]
    )

    const lines = [
      route.packingLine1,
      route.packingLine2,
      route.packingLine3,
      route.packingLine4,
    ].filter(Boolean)

    return (
      <Paper
        variant="outlined"
        sx={{ p: 1, borderRadius: 1, mb: 0.5, '&:last-child': { mb: 0 } }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {route.productCode}
            </Typography>
            <Typography
              sx={{ fontSize: '0.62rem', color: 'text.secondary' }}
              noWrap
            >
              {route.productName}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            {lines.map((l) => (
              <Chip
                key={l}
                label={l}
                size="small"
                variant="outlined"
                sx={{ height: 16, fontSize: '0.55rem' }}
              />
            ))}
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: 'text.disabled',
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        </Box>
        {expanded && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            <CompactField
              label="Max Run (h)"
              value={maxRun}
              onChange={(v) => {
                setMaxRun(v)
                handleChange('maxRunTime', v)
              }}
            />
            <CompactField
              label="Min Run (h)"
              value={minRun}
              onChange={(v) => {
                setMinRun(v)
                handleChange('minRunTime', v)
              }}
            />
            <CompactField
              label="Max Batches"
              value={maxBatches}
              onChange={(v) => {
                setMaxBatches(v)
                handleChange('maxBatches', v)
              }}
            />
            <CompactField
              label="Off-set (h)"
              value={prepTime}
              onChange={(v) => {
                setPrepTime(v)
                handleChange('prepTime', v)
              }}
            />
            <CompactField
              label="Prep Time (h)"
              value={inputPrepTime}
              onChange={(v) => {
                setInputPrepTime(v)
                handleChange('inputPrepTime', v)
              }}
            />
          </Box>
        )}
      </Paper>
    )
  }
)
KitchenProductRow.displayName = 'KitchenProductRow'

export const KitchenProductEditPanel = ({
  kitchenName,
  packagingRoutes,
  onSaveRoutes,
}: KitchenProductEditPanelProps) => {
  const pendingChanges = useRef<Record<number, Record<string, unknown>>>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [search, setSearch] = useState('')

  // Group routes by base material
  const groups = useMemo<BaseMaterialGroup[]>(() => {
    const kitchenRoutes = packagingRoutes.filter(
      (r) => r.kitchen === kitchenName
    )
    const map = new Map<string, BaseMaterialGroup>()
    for (const route of kitchenRoutes) {
      if (!map.has(route.baseProductCode)) {
        map.set(route.baseProductCode, {
          code: route.baseProductCode,
          name: route.baseProductName,
          kitchenRunRate: route.kitchenRunRate,
          products: [],
        })
      }
      map.get(route.baseProductCode)!.products.push(route)
    }
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code))
  }, [packagingRoutes, kitchenName])

  const filteredGroups = useMemo(() => {
    if (!search) return groups
    const s = search.toLowerCase()
    return groups.filter(
      (g) =>
        g.code.toLowerCase().includes(s) ||
        g.name.toLowerCase().includes(s) ||
        g.products.some(
          (p) =>
            String(p.productCode).toLowerCase().includes(s) ||
            p.productName.toLowerCase().includes(s)
        )
    )
  }, [groups, search])

  const handleRouteChange = useCallback(
    (
      routeId: number,
      field: string,
      value: string,
      fullRow: Record<string, unknown>
    ) => {
      if (!pendingChanges.current[routeId]) {
        pendingChanges.current[routeId] = { ...fullRow }
      }
      pendingChanges.current[routeId][field] = value
      setHasChanges(true)
    },
    []
  )

  const handleSave = useCallback(() => {
    if (Object.keys(pendingChanges.current).length > 0) {
      onSaveRoutes('packing-routes', pendingChanges.current)
    }
    pendingChanges.current = {}
    setHasChanges(false)
  }, [onSaveRoutes])

  const totalProducts = groups.reduce((sum, g) => sum + g.products.length, 0)

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#fafafa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
            {kitchenName}
          </Typography>
          <Chip
            label={`${groups.length} base materials · ${totalProducts} products`}
            size="small"
            sx={{ height: 20, fontSize: '0.6rem' }}
          />
        </Box>
        {hasChanges && (
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleSave}
            sx={{
              fontSize: '0.65rem',
              textTransform: 'none',
              height: 26,
              px: 1.5,
            }}
          >
            Save Changes
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #e0e0e0' }}>
        <TextField
          placeholder="Search base materials or products..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            width: '100%',
            maxWidth: 300,
            '& .MuiInputBase-root': { height: 26, fontSize: '0.7rem' },
            '& .MuiInputBase-input': { py: 0.25 },
          }}
        />
      </Box>

      {/* Base material groups */}
      <Box sx={{ maxHeight: 500, overflowY: 'auto', p: 1.5 }}>
        {filteredGroups.length === 0 && (
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              textAlign: 'center',
              py: 2,
            }}
          >
            {search
              ? `No results for "${search}"`
              : 'No products assigned to this kitchen.'}
          </Typography>
        )}
        {filteredGroups.map((group) => (
          <Paper
            key={group.code}
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              mb: 1,
              '&:last-child': { mb: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.75,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                  {group.name || group.code}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.62rem', color: 'text.secondary' }}
                >
                  {group.code}
                </Typography>
              </Box>
              <Chip
                label={`${group.kitchenRunRate} u/hr`}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.58rem' }}
              />
            </Box>
            <Divider sx={{ my: 0.5 }} />
            {group.products.map((route) => (
              <KitchenProductRow
                key={route.id}
                route={route}
                onRouteChange={handleRouteChange}
              />
            ))}
          </Paper>
        ))}
      </Box>
    </Paper>
  )
}
